import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
	FormControl,
	FormHelperText,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type React from "react";

const theme = createTheme({
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: "12px",
					".MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#DC2626",
					},
				},
				input: {
					color: "#3F3F46",
				},
				notchedOutline: {
					"& legend > span": {
						display: "none",
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					maxWidth: "310px",
					fontFamily: "Montserrat",
					fontSize: "16px",
					textWrap: "pretty",
					color: "#3F3F46",
					backgroundColor: "#FFFFFF",
					padding: "4px",
					transform: "translateY(12px) scale(1)", // Default position
					transition: "transform 200ms ease-in-out",
					"&.Mui-focused": {
						color: "#3F3F46",
						textWrap: "nowrap",
						top: 0,
						maxWidth: "100%",
						transform: "translate(12px, -10px) scale(0.75)", // Focused position
					},
					"&.MuiFormLabel-filled": {
						textWrap: "nowrap",
						top: 0,
						maxWidth: "100%",
						transform: "translate(12px, -10px) scale(0.75)", // Filled position
					},
					"&.Mui-error": {
						color: "#DC2626",
					},
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					fontSize: "24px",
					color: "#3F3F46",
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					"&.Mui-error": {
						color: "#DC2626",
					},
				},
			},
		},
	},
});

type CustomInputProps = {
	type: "email" | "number" | "password" | "search" | "text";
	label?: string;
	value?: string | number;
	name: string;
	placeholder?: string;
	errorMessage?: string;
	isShowErrorMessage?: boolean;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onToggleVisibility?: () => void;
	showPassword?: boolean;
};

const SecondCustomInput: React.FC<CustomInputProps> = ({
	type,
	label,
	value,
	name,
	placeholder,
	errorMessage,
	isShowErrorMessage,
	startIcon,
	endIcon,
	onChange,
	onBlur,
	onToggleVisibility,
	showPassword,
}) => {
	return (
		<ThemeProvider theme={theme}>
			<FormControl
				fullWidth
				sx={{ marginBottom: errorMessage ? "20px" : "0", height: "56px" }}
				variant="outlined"
				error={!!errorMessage}
			>
				{label ? (
					<InputLabel
						htmlFor={`${name}-input`}
						sx={{
							...(name === "registration" ? { top: "-11px" } : {}),
							...(startIcon
								? { transform: "translate(45px, 12px) scale(1)" }
								: { transform: "translate(12px, 12px) scale(1)" }),
						}}
						shrink
					>
						{label}
					</InputLabel>
				) : null}
				<OutlinedInput
					id={`${name}-input`}
					type={showPassword && type === "password" ? "text" : type}
					value={value}
					name={name}
					placeholder={placeholder}
					onChange={onChange}
					onBlur={onBlur}
					startAdornment={
						startIcon && (
							<InputAdornment position="start">{startIcon}</InputAdornment>
						)
					}
					endAdornment={
						endIcon ? (
							<InputAdornment position="end">{endIcon}</InputAdornment>
						) : (
							type === "password" && (
								<InputAdornment position="end">
									<IconButton
										onClick={onToggleVisibility}
										onMouseDown={(e) => e.preventDefault()}
										edge="end"
										sx={{ color: "#3F3F46" }}
									>
										{showPassword ? (
											<VisibilityOffOutlinedIcon />
										) : (
											<VisibilityOutlinedIcon />
										)}
									</IconButton>
								</InputAdornment>
							)
						)
					}
					label={label}
				/>
				{errorMessage && isShowErrorMessage && (
					<FormHelperText>{errorMessage}</FormHelperText>
				)}
			</FormControl>
		</ThemeProvider>
	);
};

export default SecondCustomInput;
